import { request } from "../../../utils";
import { CONFIG_TYPE } from "../../../utils/constant";
import {
  CreateWalletLinkPayload,
  CreatedWalletLink,
  WalletLinkBankAttempt,
  UpdateWalletLinkPayload,
  WalletLinkConfig,
  WalletLinkDetail,
  WalletLinkItem,
  WalletLinkMomoAttempt,
  WalletLinkNetwork,
  WalletLinkPublic,
} from "../types";

export const WALLET_LINK_API = "/wallet/payment-link";

// The link the owner shares with the payer. Route is registered in README.md.
export const buildPayUrl = (token: string) =>
  `${window.location.origin}/wallet-pay/${token}`;

export const createWalletLink = async (
  payload: CreateWalletLinkPayload
): Promise<CreatedWalletLink> => {
  const res = await request.post({ url: WALLET_LINK_API, data: payload });
  return res.data as CreatedWalletLink;
};

export const listWalletLinks = async (): Promise<WalletLinkItem[]> => {
  const res = await request.get({ url: WALLET_LINK_API });
  return (res.data || []) as WalletLinkItem[];
};

export const getWalletLink = async (id: string): Promise<WalletLinkDetail> => {
  const res = await request.get({ url: `${WALLET_LINK_API}/${id}` });
  return res.data as WalletLinkDetail;
};

export const updateWalletLink = async (
  id: string,
  payload: UpdateWalletLinkPayload
): Promise<WalletLinkDetail> => {
  const res = await request.post({
    url: `${WALLET_LINK_API}/${id}/update`,
    data: payload,
  });
  return res.data as WalletLinkDetail;
};

export const cancelWalletLink = async (id: string) => {
  const res = await request.post({
    url: `${WALLET_LINK_API}/${id}/cancel`,
    data: {},
  });
  return res.data;
};

// Same endpoint and type the Fund Wallet modal uses for deposit fees.
export const getWalletLinkFee = async (
  amount: number,
  iso2: string
): Promise<number> => {
  const res = await request.get<any>({
    url: "/utility/transaction-fee",
    params: {
      amount,
      iso2,
      type: CONFIG_TYPE.DEPOSIT_FEE,
    },
  });
  return Number(res.data?.fee) || 0;
};

export const getWalletLinkSummary = async (token: string) => {
  const res = await request.get({
    url: `${WALLET_LINK_API}/p/${token}`,
    auth: false,
  });
  return res.data as WalletLinkPublic;
};

export const getWalletLinkStatus = async (token: string) => {
  const res = await request.get({
    url: `${WALLET_LINK_API}/p/${token}/status`,
    auth: false,
  });
  return res.data as WalletLinkPublic;
};

export const getWalletLinkConfig = async (token: string) => {
  const res = await request.get({
    url: `${WALLET_LINK_API}/p/${token}/config`,
    auth: false,
  });
  return res.data as WalletLinkConfig;
};

export const getWalletLinkNetworks = async (
  token: string
): Promise<WalletLinkNetwork[]> => {
  const config = await getWalletLinkConfig(token);
  return (config?.networks || []).map((n: any) => ({
    code: n.code,
    name: n.name,
    logo: n.logo,
  }));
};

export const startMomoPayment = async (
  token: string,
  body: { network: string; phoneNumber: string }
) => {
  const res = await request.post({
    url: `${WALLET_LINK_API}/p/${token}/momo`,
    data: body,
    auth: false,
  });
  return res.data as WalletLinkMomoAttempt;
};

export const declineWalletLink = async (token: string) => {
  const res = await request.post({
    url: `${WALLET_LINK_API}/p/${token}/decline`,
    data: {},
    auth: false,
  });
  return res.data as { status: "DECLINED" };
};

export const startBankPayment = async (token: string) => {
  const res = await request.post({
    url: `${WALLET_LINK_API}/p/${token}/bank`,
    data: {},
    auth: false,
  });
  return res.data as WalletLinkBankAttempt;
};

// The API answers every unavailable link (unknown, cancelled, expired) with 404.
export const isNotFound = (err: any) =>
  err?.httpStatusCode === 404 ||
  err?.statusCode === 404 ||
  /not found/i.test(String(err?.message || ""));
  